import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import type { AppArcOrganization, ArcPreviewArc } from '@/api/generated';
import { Button } from '@/components/ui/Button';
import { initials } from '@/lib/names';
import { testIdAttr } from '@/lib/testIds';

import {
  DONATE_LABEL,
  inertIntroHtml,
  SUPPORT_LABEL,
  supporterWords,
  VOLUNTEER_LABEL,
} from './arcPreview';

/** The cover band's height in the frame, in CSS pixels. */
const COVER_HEIGHT = 120;
const LOGO_SIZE = 64;

/** The introduction's own elements, drawn as body text; the button as a dimmed button. */
const INTRO_STYLE = {
  overflowWrap: 'anywhere',
  '& > :first-of-type': { mt: 0 },
  '& h2, & h3': { fontSize: '1rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
  '& p': { my: 0.5 },
  '& img': { display: 'block', maxWidth: '100%', borderRadius: 1 },
  '& .cta-button': {
    display: 'inline-block',
    px: 2,
    py: 0.75,
    borderRadius: 1,
    bgcolor: 'action.disabledBackground',
    color: 'text.disabled',
    fontWeight: 600,
  },
} as const;

interface ArcPreviewHeaderProps {
  arc: ArcPreviewArc;
  organization: AppArcOrganization;
}

/**
 * The top of the ARC as the app draws it (organization-arc, group 8; B19,
 * D25): the cover, the logo (or the initials), the name, the description,
 * the live supporter count, the buttons, and the introduction. Every button
 * is switched off, because Preview opens nothing (D19) and the Admin is not
 * a supporter (D25).
 */
export function ArcPreviewHeader({ arc, organization }: ArcPreviewHeaderProps) {
  return (
    <Box
      component="header"
      data-testid="arc-preview-header-group"
      sx={{ display: 'grid', gap: 1.5 }}
    >
      {arc.cover_image ? (
        <Box
          component="img"
          src={arc.cover_image.url}
          alt={`${arc.title} cover image`}
          data-testid="arc-preview-cover-image"
          sx={{
            display: 'block',
            width: '100%',
            height: COVER_HEIGHT,
            objectFit: 'cover',
            borderRadius: 2,
          }}
        />
      ) : (
        <Box
          aria-hidden
          data-testid="arc-preview-cover-empty"
          sx={{ height: COVER_HEIGHT, borderRadius: 2, bgcolor: 'action.hover' }}
        />
      )}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
        {arc.logo ? (
          <Avatar
            src={arc.logo.url}
            alt={`${arc.title} logo`}
            // On the <img> itself, which carries the address and the name.
            slotProps={{ img: testIdAttr('arc-preview-logo-image') }}
            sx={{
              width: LOGO_SIZE,
              height: LOGO_SIZE,
              border: 1.5,
              borderColor: 'text.primary',
            }}
          />
        ) : (
          <Avatar
            aria-hidden
            data-testid="arc-preview-logo-empty"
            sx={{
              width: LOGO_SIZE,
              height: LOGO_SIZE,
              bgcolor: 'background.paper',
              color: 'primary.main',
              border: 1.5,
              borderColor: 'text.primary',
              fontSize: 24,
            }}
          >
            {initials(arc.title)}
          </Avatar>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h2"
            variant="h6"
            sx={{ overflowWrap: 'anywhere' }}
            data-testid="arc-preview-title"
          >
            {arc.title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="arc-preview-supporter-count"
          >
            {supporterWords(organization.supporter_count)}
          </Typography>
        </Box>
      </Box>
      {arc.description ? (
        <Typography
          variant="body2"
          sx={{ overflowWrap: 'anywhere' }}
          data-testid="arc-preview-description"
        >
          {arc.description}
        </Typography>
      ) : null}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="primary"
          size="sm"
          disabled
          data-testid="arc-preview-support-button"
        >
          {SUPPORT_LABEL}
        </Button>
        {arc.header_actions.donate.enabled ? (
          <Button
            variant="secondary"
            size="sm"
            disabled
            data-testid="arc-preview-donate-button"
          >
            {DONATE_LABEL}
          </Button>
        ) : null}
        {arc.header_actions.volunteer.enabled ? (
          <Button
            variant="secondary"
            size="sm"
            disabled
            data-testid="arc-preview-volunteer-button"
          >
            {VOLUNTEER_LABEL}
          </Button>
        ) : null}
      </Box>
      {arc.intro_html ? (
        <Box
          data-testid="arc-preview-intro"
          sx={INTRO_STYLE}
          // The backend's allow-listed HTML (`rich_text.render`), with every
          // link made inert: Preview opens nothing (D19).
          dangerouslySetInnerHTML={{ __html: inertIntroHtml(arc.intro_html) }}
        />
      ) : null}
    </Box>
  );
}
